import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc,
  serverTimestamp, query, where, orderBy, limit, getDocs, writeBatch,
} from 'firebase/firestore';

import TopicGatePanel, { TierChip } from './TopicGatePanel';

const GENERATE_URL = 'https://generateandpublish-mqcgqufdma-uc.a.run.app';
const AUTO_LINKER_RULES_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/getAutoLinkerRules';
const OUTRANK_TITLES_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/generateOutrankTitles';
const POST_GSC_DETAIL_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/getPostGscDetail';
const BLOG_GSC_OVERVIEW_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/getBlogGscOverview';
const ARTICLE_TYPES = ['Informational', 'How-To Guide', 'FAQ', 'Listicle'];
const WORD_COUNTS = [800, 1000, 1200, 1500, 2000];
const DEFAULT_WORD_COUNT = 1500;
const ALL_DAYS = ['Su', 'M', 'T', 'W', 'Th', 'F', 'Sa'];
const DAY_LABELS = { Su: 'Sun', M: 'Mon', T: 'Tue', W: 'Wed', Th: 'Thu', F: 'Fri', Sa: 'Sat' };
const QUEUE_MAX = 15;
const QUEUE_WARN = 5;

function formatScheduled(iso) {
  if (!iso) return '—';
  // Pinned to Pacific — the scheduler decides blog days in Pacific, so the day
  // shown here has to be the day it will actually publish, not the viewer's.
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'America/Los_Angeles', weekday: 'short', month: 'short', day: 'numeric',
  });
}

// Mirror the backend's normalizeUrlForMatch (functions/index.js) so post URLs line up with GSC page rows
function normUrl(u) {
  return (u || '').replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '').toLowerCase();
}

function fmtNum(v) {
  return v == null ? '—' : Number(v).toLocaleString('en-US');
}

function StatusBadge({ status }) {
  const map = {
    planned:    { label: '◎ Planned',    style: { background: 'rgba(99,102,241,.1)', color: '#818cf8', borderColor: '#4f46e533' } },
    generating: { label: '⟳ Generating', style: { background: '#1a1000', color: '#fb923c', borderColor: '#c2410c44' } },
    future:     { label: '◷ Scheduled',  style: { background: '#1a1500', color: '#eab308', borderColor: '#a16207aa' } },
  };
  const s = map[status] || map.planned;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, border: '1px solid', ...s.style }}>
      {s.label}
    </span>
  );
}

// Blog slots are Pacific days, not browser-local ones — these mirror the
// ptDateKey / ptDayCode / ptSlotInstant helpers in functions/index.js so a
// title added here lands on exactly the instant the scheduler expects.
const PT_TZ = 'America/Los_Angeles';
const DAY_CODES = ['Su', 'M', 'T', 'W', 'Th', 'F', 'Sa'];
const PT_DAY_FROM_SHORT = { Sun: 'Su', Mon: 'M', Tue: 'T', Wed: 'W', Thu: 'Th', Fri: 'F', Sat: 'Sa' };

function ptDateKey(d = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: PT_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}

function ptDayCode(d = new Date()) {
  return PT_DAY_FROM_SHORT[
    new Intl.DateTimeFormat('en-US', { timeZone: PT_TZ, weekday: 'short' }).format(d)
  ];
}

// 16:00Z is 9am PDT / 8am PST — the same calendar date in Pacific and UTC.
function ptSlotInstant(dateKey) {
  return new Date(`${dateKey}T16:00:00Z`);
}

// `takenDates` holds ptDateKey strings.
function nextAvailableDate(days, takenDates = new Set()) {
  const targets = (days || []).filter(d => DAY_CODES.includes(d));
  if (targets.length === 0) return null;
  // Anchor at ~1pm Pacific so adding whole days never straddles midnight and
  // skips or repeats a calendar day across a DST boundary.
  const anchor = new Date(`${ptDateKey(new Date())}T20:00:00Z`);
  for (let i = 1; i <= 365; i++) {
    const probe = new Date(anchor.getTime() + i * 86400000);
    const key = ptDateKey(probe);
    if (targets.includes(ptDayCode(probe)) && !takenDates.has(key)) {
      return ptSlotInstant(key);
    }
  }
  return null;
}

// Resolve with the job's result once generateOutrankTitles marks it done.
// Gives up after the function's own 20-minute timeout plus some slack.
function waitForTitleJob(jobRef, timeoutMs = 22 * 60 * 1000) {
  return new Promise((resolve, reject) => {
    let unsub = () => {};
    const finish = (fn, val) => { clearTimeout(timer); unsub(); fn(val); };
    const timer = setTimeout(() => finish(reject, new Error('Generation is taking unusually long — check the queue in a few minutes before trying again.')), timeoutMs);
    unsub = onSnapshot(jobRef, snap => {
      if (!snap.exists()) {
        // Server-confirmed missing = the request never reached the function.
        if (!snap.metadata.fromCache) finish(reject, new Error('Lost connection before generation started — try again.'));
        return;
      }
      const job = snap.data();
      if (job.status === 'done') finish(resolve, job);
      else if (job.status === 'error') finish(reject, new Error(job.error || 'Title generation failed'));
    }, err => finish(reject, err));
  });
}

export default function ContentQueue() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [client, setClient] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [gscSnapshot, setGscSnapshot] = useState(null);
  // Trailing-28-day Search Console overview (one call for the whole site, cached server-side)
  const [blogGsc, setBlogGsc] = useState(null);
  const [blogGscLoading, setBlogGscLoading] = useState(false);
  const [blogGscError, setBlogGscError] = useState(null);
  const [expandedUrl, setExpandedUrl] = useState(null);
  const [detailByUrl, setDetailByUrl] = useState({});

  // Bulk scheduling form
  const [bulkTitles, setBulkTitles] = useState('');
  const [defaultArticleType, setDefaultArticleType] = useState('Informational');
  const [bulkWordCount, setBulkWordCount] = useState(1500);
  const [bulkLanguage, setBulkLanguage] = useState('en');
  const [bulkAdding, setBulkAdding] = useState(false);
  const [bulkError, setBulkError] = useState('');

  // Per-row generation
  const [generatingId, setGeneratingId] = useState(null);
  const [genError, setGenError] = useState('');

  // AI title generation (Outrank)
  const [aiCount, setAiCount] = useState(10);
  const [aiKeywords, setAiKeywords] = useState('');
  const [aiCurrentEvents, setAiCurrentEvents] = useState(true);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiSuccess, setAiSuccess] = useState('');

  // Per-row detail expansion
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [detailTexts, setDetailTexts] = useState({});
  const [titleTexts, setTitleTexts] = useState({});
  const [wordCounts, setWordCounts] = useState({});
  const [savingDetailId, setSavingDetailId] = useState(null);

  // Drag-and-drop
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Auto-linker rules for cannibalization detection
  const [autoLinkerRules, setAutoLinkerRules] = useState([]);
  const [rulesError, setRulesError] = useState('');

  // Load clients
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'clients'), orderBy('businessName')),
      snap => setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => !c.isArchived))
    );
    return unsub;
  }, []);

  // Sync client object when selection changes
  useEffect(() => {
    const c = clients.find(c => c.id === selectedClientId) || null;
    setClient(c);
    setPosts([]);
    setBulkTitles('');
    setBulkError('');
    setExpandedPostId(null);
    setDetailTexts({});
    setTitleTexts({});
    setWordCounts({});
    setGenError('');
    setAutoLinkerRules([]);
    setRulesError('');
    setAiKeywords('');
    setAiError('');
    setAiSuccess('');
  }, [selectedClientId, clients]);

  // Fetch auto-linker rules when client has app password credentials
  useEffect(() => {
    const c = clients.find(c => c.id === selectedClientId) || null;
    if (!c?.wpUsername || !c?.wpAppPassword || !c?.clientUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const idToken = await auth.currentUser?.getIdToken();
        if (!idToken || cancelled) return;
        const res = await fetch(AUTO_LINKER_RULES_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ siteUrl: c.clientUrl, wpUsername: c.wpUsername, wpAppPassword: c.wpAppPassword }),
        });
        const data = await res.json();
        if (cancelled) return;
        if (data.error) setRulesError(data.error);
        setAutoLinkerRules(data.rules || []);
      } catch (err) {
        if (!cancelled) setRulesError('Could not load auto-linker rules.');
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId, clients]);

  // Load posts for selected client
  useEffect(() => {
    if (!selectedClientId) return;
    setLoadingPosts(true);
    const unsub = onSnapshot(
      query(collection(db, 'contentQueue'), where('clientId', '==', selectedClientId)),
      snap => {
        setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoadingPosts(false);
      },
      err => { console.error('contentQueue snapshot error:', err); setLoadingPosts(false); }
    );
    return unsub;
  }, [selectedClientId]);

  // Load the latest stored GSC snapshot (from the monthly pull) to show per-post metrics
  useEffect(() => {
    if (!selectedClientId) { setGscSnapshot(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'clients', selectedClientId, 'gsc'),
          orderBy('pulledAt', 'desc'),
          limit(1),
        ));
        if (!cancelled) setGscSnapshot(snap.empty ? null : snap.docs[0].data());
      } catch (e) {
        console.error('gsc snapshot load error:', e);
        if (!cancelled) setGscSnapshot(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId]);

  // Trailing-28-day Search Console metrics for every page on the site. One
  // backend call, served from a cache the daily scheduler warms, so this is
  // normally instant. This is what the inline per-post numbers read from —
  // the monthly snapshot above is only a fallback now.
  const loadBlogGsc = React.useCallback(async (clientId, force = false) => {
    if (!clientId) return;
    setBlogGscLoading(true);
    setBlogGscError(null);
    try {
      if (!auth.currentUser) throw new Error('You are signed out — sign in again.');
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(BLOG_GSC_OVERVIEW_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ clientId, force }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.details || 'Failed to load Search Console overview');
      setBlogGsc(data.unavailable ? null : data);
    } catch (err) {
      setBlogGsc(null);
      setBlogGscError(err.message);
    } finally {
      setBlogGscLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedClientId) { setBlogGsc(null); setBlogGscError(null); return; }
    setBlogGsc(null);
    loadBlogGsc(selectedClientId);
  }, [selectedClientId, loadBlogGsc]);

  // Note: the blog queue is now refilled automatically by the topUpContentQueues
  // scheduled Cloud Function (keeps every active client at QUEUE_TARGET). The old
  // "nudge Tiana when the queue runs low" note/task was removed with that change.

  const scheduleDays = client?.blogDays?.length ? client.blogDays : ['M', 'Th'];

  // Scheduled queue: only items still awaiting generation (planned/generating),
  // sorted by date, capped at QUEUE_MAX. Once generated, a post becomes
  // 'draft' or 'publish' and moves to Published History instead of lingering here.
  const scheduledQueue = posts
    .filter(p => ['planned', 'generating'].includes(p.status))
    .sort((a, b) => new Date(a.scheduledFor || 0) - new Date(b.scheduledFor || 0))
    .slice(0, QUEUE_MAX);

  // Published history: rolling 90-day window. Merges live queue records with
  // the permanent publishedTitlesLog on the client doc (which survives the
  // "Clear list" cleanup on the Publishing page), deduped by URL/title.
  const HISTORY_DAYS = 90;
  const historyCutoff = Date.now() - HISTORY_DAYS * 86400000;
  const publishedHistory = (() => {
    const queueEntries = posts
      .filter(p => p.postUrl)
      .map(p => ({
        id: p.id,
        title: p.title,
        postUrl: p.postUrl,
        isDraft: p.status === 'draft',
        dateMs: (p.generatedAt?.seconds || 0) * 1000,
      }));
    const logEntries = (client?.publishedTitlesLog || []).map((e, i) => ({
      id: `log_${i}`,
      title: e.title,
      postUrl: e.postUrl,
      isDraft: e.status === 'draft',
      dateMs: e.date ? new Date(e.date).getTime() : 0,
    }));
    const seen = new Set();
    return [...queueEntries, ...logEntries]
      .filter(e => {
        const key = e.postUrl || e.title;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return e.dateMs >= historyCutoff;
      })
      .sort((a, b) => b.dateMs - a.dateMs);
  })();

  // Inline per-post GSC metrics, keyed by normalized post URL.
  //
  // Prefer the trailing-28-day overview: it ends 3 days ago (GSC's own lag) and
  // uses the same window as the expandable "queries" panel, so the two numbers
  // agree. The monthly snapshot is a 90-day window pulled on the 2nd, so its
  // window can end before a recent post existed — it is a fallback only, used
  // when the live overview can't load, so rows never go blank.
  const gscSource = blogGsc?.pages ? 'recent' : (gscSnapshot ? 'monthly' : null);
  const gscByUrl = (() => {
    const m = new Map();
    if (gscSource === 'recent') {
      for (const p of blogGsc.pages) m.set(normUrl(p.url), { clicks: p.clicks, impressions: p.impressions, position: p.position });
    } else if (gscSource === 'monthly') {
      for (const p of (gscSnapshot.pages || [])) m.set(normUrl(p.page), { clicks: p.clicks, impressions: p.impressions, position: p.position });
      for (const b of (gscSnapshot.blogPerformance || [])) m.set(normUrl(b.url), { clicks: b.clicks, impressions: b.impressions, position: b.position });
    }
    return m;
  })();

  // Live per-post GSC detail (the queries a post ranks for), fetched on expand
  const toggleDetail = async (postUrl) => {
    if (!postUrl) return;
    if (expandedUrl === postUrl) { setExpandedUrl(null); return; }
    setExpandedUrl(postUrl);
    if (detailByUrl[postUrl]?.data || detailByUrl[postUrl]?.loading) return;
    setDetailByUrl(prev => ({ ...prev, [postUrl]: { loading: true } }));
    try {
      if (!auth.currentUser) throw new Error('You are signed out — sign in again.');
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(POST_GSC_DETAIL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ clientId: selectedClientId, postUrl }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.details || 'Failed to load Search Console detail');
      setDetailByUrl(prev => ({ ...prev, [postUrl]: { data } }));
    } catch (err) {
      setDetailByUrl(prev => ({ ...prev, [postUrl]: { error: err.message } }));
    }
  };

  const queueCount = scheduledQueue.length;
  const isQueueLow = queueCount > 0 && queueCount <= QUEUE_WARN;

  const [clearingError, setClearingError] = useState(null);

  const handleClearGenError = async (postId) => {
    setClearingError(postId);
    await updateDoc(doc(db, 'contentQueue', postId), { genError: null });
    setClearingError(null);
  };

  // Derive issues list for the alert banner
  const issues = client ? (() => {
    const list = [];
    if (client.publishPlatform !== 'webflow' && !client.wpApiKey && !(client.wpUsername && client.wpAppPassword))
      list.push({ level: 'error', msg: 'No WordPress credentials — add a Publisher API Key or WP Username + Application Password in the client profile.' });
    if (client.publishPlatform === 'webflow' && (!client.webflowApiToken || !client.webflowCollectionId))
      list.push({ level: 'error', msg: 'Webflow credentials incomplete — add an API Token and Collection ID in the client profile.' });
    if (!client.clientUrl)
      list.push({ level: 'error', msg: 'No site URL configured — publishing is disabled. Add it in the client profile.' });
    const failedPosts = posts.filter(p => p.genError && p.status === 'planned');
    if (failedPosts.length > 0) {
      const latest = failedPosts.sort((a, b) => (b.generatedAt?.seconds || 0) - (a.generatedAt?.seconds || 0))[0];
      list.push({ level: 'error', msg: `Last publish attempt failed: ${latest.genError}`, clearId: latest.id });
    }
    if (!loadingPosts && queueCount === 0)
      list.push({ level: 'warning', msg: 'Queue is empty — no articles are scheduled for this client.' });
    else if (isQueueLow)
      list.push({ level: 'warning', msg: `Queue is low — only ${queueCount} article${queueCount !== 1 ? 's' : ''} remaining. Schedule more soon.` });
    return list;
  })() : [];

  // Parse textarea into title array
  const parsedTitles = bulkTitles
    .split('\n')
    .map(t => t.trim())
    .filter(Boolean)
    .slice(0, 10);

  function getCannibalizationWarning(title) {
    if (!autoLinkerRules.length || !title) return null;
    const haystack = title.toLowerCase();
    for (const rule of autoLinkerRules) {
      const needle = rule.keyword.toLowerCase();
      if (haystack.includes(needle) || needle.includes(haystack.split(' ').slice(0, 3).join(' '))) {
        return { keyword: rule.keyword, url: rule.url };
      }
    }
    return null;
  }

  function extractLocation(name, url) {
    const kws = ['salinas', 'monterey', 'carmel', 'seaside', 'marina', 'pacific grove', 'king city', 'hollister', 'gilroy', 'watsonville', 'santa cruz'];
    const hay = (name + ' ' + url).toLowerCase();
    for (const kw of kws) {
      if (hay.includes(kw)) return kw.replace(/\b\w/g, c => c.toUpperCase()) + ', CA';
    }
    return 'Monterey County, CA';
  }

  const handleBulkSchedule = async () => {
    if (!parsedTitles.length) { setBulkError('Enter at least one title.'); return; }
    setBulkError('');
    setBulkAdding(true);

    const takenDates = new Set(
      scheduledQueue.map(p => p.scheduledFor ? ptDateKey(new Date(p.scheduledFor)) : '').filter(Boolean)
    );

    for (const title of parsedTitles) {
      const nextDate = nextAvailableDate(scheduleDays, takenDates);
      if (!nextDate) break;
      await addDoc(collection(db, 'contentQueue'), {
        clientId: selectedClientId,
        clientName: client.businessName,
        title,
        keyword: '',
        articleType: defaultArticleType,
        language: bulkLanguage,
        wordCount: bulkWordCount,
        status: 'planned',
        scheduledFor: nextDate.toISOString(),
        siteUrl: client.clientUrl,
        additionalDetails: '',
        postId: null,
        postUrl: null,
        createdAt: serverTimestamp(),
      });
      takenDates.add(ptDateKey(nextDate));
    }

    setBulkTitles('');
    setBulkAdding(false);
  };

  const handleAiGenerate = async () => {
    setAiError('');
    setAiSuccess('');
    const slotsLeft = QUEUE_MAX - queueCount;
    if (slotsLeft <= 0) { setAiError(`Queue is full (${QUEUE_MAX}). Remove or publish some articles first.`); return; }
    const requestCount = Math.min(aiCount, slotsLeft);

    setAiGenerating(true);
    try {
      if (!auth.currentUser) throw new Error('You are signed out — sign in again to generate.');
      const idToken = await auth.currentUser.getIdToken();
      const parsedKeywords = aiKeywords.split('\n').map(k => k.trim()).filter(Boolean);
      // The server schedules the titles itself and reports progress on this
      // job doc. A 5-10 title run takes longer than the browser holds a fetch
      // open, so when the connection drops we follow the job instead.
      const jobRef = doc(collection(db, 'clients', selectedClientId, 'titleJobs'));
      let data;
      try {
        const res = await fetch(OUTRANK_TITLES_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({
            clientId: selectedClientId,
            count: requestCount,
            keywords: parsedKeywords,
            doNotTarget: autoLinkerRules.map(r => r.keyword),
            includeCurrentEvents: aiCurrentEvents,
            jobId: jobRef.id,
            fallbackArticleType: defaultArticleType,
            fallbackWordCount: bulkWordCount,
          }),
        });
        data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) throw new Error(data.error || 'Title generation failed');
      } catch (err) {
        if (!(err instanceof TypeError)) throw err; // a real server error, not a dropped connection
        setAiSuccess('Still generating on the server — titles will appear in the queue below when ready. Safe to leave this page.');
        data = await waitForTitleJob(jobRef);
        setAiSuccess('');
      }

      setAiKeywords('');
      const added = data.added || 0;
      if (added === 0) throw new Error(data.cappedByQueue ? 'Queue filled up while generating — no titles were added.' : 'No titles were returned — try again.');
      const heldNote = data.held ? ` · ${data.held} long-shot${data.held !== 1 ? 's' : ''} held for review above` : '';
      const cappedNote = requestCount < aiCount || data.cappedByQueue ? ' (capped — queue limit)' : '';
      setAiSuccess(`✓ ${added} blog${added !== 1 ? 's' : ''} generated and scheduled${cappedNote}${heldNote}`);
    } catch (err) {
      setAiSuccess('');
      setAiError(err.message);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Remove this article from the queue?')) return;
    await deleteDoc(doc(db, 'contentQueue', postId));
  };

  const handleUpdateArticleType = async (postId, newType) => {
    await updateDoc(doc(db, 'contentQueue', postId), { articleType: newType });
  };

  const handleSaveDetail = async (post) => {
    setSavingDetailId(post.id);
    const newTitle = (titleTexts[post.id] ?? post.title ?? '').trim();
    await updateDoc(doc(db, 'contentQueue', post.id), {
      title: newTitle || post.title,
      additionalDetails: detailTexts[post.id] ?? '',
      wordCount: wordCounts[post.id] ?? post.wordCount ?? DEFAULT_WORD_COUNT,
    });
    setSavingDetailId(null);
  };

  const handleGenerateFromPlan = async (post) => {
    if (client.publishPlatform !== 'webflow' && !client.wpApiKey && !(client.wpUsername && client.wpAppPassword)) { setGenError('No WordPress credentials on this client record.'); return; }
    if (client.publishPlatform === 'webflow' && (!client.webflowApiToken || !client.webflowCollectionId)) { setGenError('Webflow credentials incomplete — check the client profile.'); return; }
    setGenError('');
    setGeneratingId(post.id);
    await updateDoc(doc(db, 'contentQueue', post.id), { status: 'generating' });
    try {
      const payload = {
        keyword: post.keyword || post.title,
        articleType: post.articleType || 'Informational',
        language: post.language === 'es' ? 'es' : 'en',
        location: extractLocation(client.businessName, client.clientUrl),
        wordCount: post.wordCount || 1500,
        clientName: client.businessName,
        siteUrl: client.clientUrl.replace(/\/$/, ''),
        wpApiKey: client.wpApiKey,
        wpUsername: client.wpUsername || '',
        wpAppPassword: client.wpAppPassword || '',
        publishPlatform: client.publishPlatform || 'wordpress',
        webflowApiToken: client.webflowApiToken || '',
        webflowSiteId: client.webflowSiteId || '',
        webflowCollectionId: client.webflowCollectionId || '',
        status: client.autoPublish ? 'publish' : 'draft',
        suggestedTitle: post.title,
        brandContext: client.brandVoice || '',
        imageStyle: client.imageStyle || 'illustrated',
        additionalDetails: post.additionalDetails || '',
        queueDocId: post.id,
      };
      if (!auth.currentUser) throw new Error('You are signed out — sign in again to publish.');
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(GENERATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.wp_error || 'Generation failed');
    } catch (err) {
      setGenError(err.message);
      await updateDoc(doc(db, 'contentQueue', post.id), { status: 'planned' }).catch(() => {});
    } finally {
      setGeneratingId(null);
    }
  };

  // Drag-and-drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder items
    const newOrder = [...scheduledQueue];
    const [moved] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, moved);

    // Extract dates in ascending order from the original queue
    const dates = scheduledQueue
      .map(p => p.scheduledFor)
      .filter(Boolean)
      .sort((a, b) => new Date(a) - new Date(b));

    // Reassign dates to the new item order
    const batch = writeBatch(db);
    newOrder.forEach((post, i) => {
      if (dates[i] !== undefined) {
        batch.update(doc(db, 'contentQueue', post.id), { scheduledFor: dates[i] });
      }
    });
    await batch.commit();

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const hasWpKey = client?.publishPlatform === 'webflow'
    ? !!(client?.webflowApiToken && client?.webflowCollectionId)
    : !!(client?.wpApiKey || (client?.wpUsername && client?.wpAppPassword));

  return (
    <div className="page" style={{ maxWidth: 1320 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Content Queue</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Schedule and publish blog articles across client sites</p>
        </div>
      </div>

      {/* Client Selector */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.4px', whiteSpace: 'nowrap' }}>
          Select Client
        </label>
        <select
          value={selectedClientId}
          onChange={e => setSelectedClientId(e.target.value)}
          style={{ minWidth: 280, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 7, padding: '8px 12px', fontSize: 14, color: 'var(--text-primary)', outline: 'none' }}
        >
          <option value="">— Choose a client —</option>
          {clients
            .filter(c => (c.services || []).includes('Blog Optimization') || !c.noContentQueue)
            .map(c => <option key={c.id} value={c.id}>{c.businessName}</option>)}
        </select>
        {client && (
          <>
            {client.clientUrl && (
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px' }}>
                🌐 {client.clientUrl.replace(/https?:\/\//, '').replace(/\/$/, '')}
              </span>
            )}
            <span style={{ fontSize: 12, borderRadius: 20, padding: '4px 12px', border: '1px solid', ...(hasWpKey ? { background: '#0a1f12', borderColor: '#16a34a44', color: '#4ade80' } : { background: '#1a0a0a', borderColor: '#7f1d1d55', color: '#f87171' }) }}>
              {hasWpKey ? '✓ Publisher Connected' : '✗ No WP API Key — edit client to add one'}
            </span>
          </>
        )}
      </div>

      {/* Topic gate: held long-shots, starvation, site-authority flag. Renders nothing when there is nothing to show. */}
      {client && (
        <TopicGatePanel
          clientId={selectedClientId}
          client={client}
          scheduleDays={scheduleDays}
          nextAvailableDate={nextAvailableDate}
          ptDateKey={ptDateKey}
          takenDateKeys={scheduledQueue.map(p => p.scheduledFor ? ptDateKey(new Date(p.scheduledFor)) : '').filter(Boolean)}
        />
      )}

      {/* Brand strip */}
      {client && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 20px', marginBottom: 20, display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 5 }}>Business</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{client.businessName}</div>
            {client.clientUrl && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{client.clientUrl.replace(/https?:\/\//, '').replace(/\/$/, '')}</div>}
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 6 }}>On Generate</div>
            <span style={{ fontSize: 12, padding: '4px 12px', borderRadius: 20, border: '1px solid', ...(client.autoPublish ? { background: '#0a1f12', borderColor: '#16a34a44', color: '#4ade80' } : { background: 'var(--bg-primary)', borderColor: 'var(--border)', color: 'var(--text-secondary)' }) }}>
              {client.autoPublish ? '⚡ Auto-Publish' : '◎ Save as Draft'}
            </span>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 6 }}>Blog Schedule</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {ALL_DAYS.map(d => (
                <span key={d} style={{ fontSize: 11, fontWeight: 600, padding: '3px 7px', borderRadius: 5, border: '1px solid', ...(scheduleDays.includes(d) ? { background: '#0a1f12', borderColor: '#16a34a44', color: '#4ade80' } : { background: 'var(--bg-primary)', borderColor: 'var(--border)', color: 'var(--text-muted)' }) }}>
                  {DAY_LABELS[d]}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {issues.length > 0 && (
        <div style={{ marginBottom: 20, borderRadius: 10, overflow: 'hidden', border: `1px solid ${issues.some(i => i.level === 'error') ? '#7f1d1d88' : '#a1620788'}` }}>
          <div style={{ padding: '10px 16px', background: issues.some(i => i.level === 'error') ? '#2a0a0a' : '#1a1000', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14 }}>{issues.some(i => i.level === 'error') ? '🚨' : '⚠️'}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: issues.some(i => i.level === 'error') ? '#fca5a5' : '#fbbf24', textTransform: 'uppercase', letterSpacing: '.5px' }}>
              {issues.length} Issue{issues.length !== 1 ? 's' : ''} Detected — {client?.businessName}
            </span>
          </div>
          {issues.map((issue, idx) => (
            <div key={idx} style={{ padding: '10px 16px', background: issue.level === 'error' ? '#1a0808' : '#110d00', borderTop: idx === 0 ? 'none' : `1px solid ${issue.level === 'error' ? '#7f1d1d44' : '#a1620744'}`, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ fontSize: 11, marginTop: 1, flexShrink: 0, color: issue.level === 'error' ? '#f87171' : '#eab308' }}>
                {issue.level === 'error' ? '✕' : '!'}
              </span>
              <span style={{ fontSize: 13, color: issue.level === 'error' ? '#fca5a5' : '#fde68a', lineHeight: 1.5, flex: 1 }}>{issue.msg}</span>
              {issue.clearId && (
                <button
                  onClick={() => handleClearGenError(issue.clearId)}
                  disabled={clearingError === issue.clearId}
                  style={{ fontSize: 11, color: '#f87171', background: 'transparent', border: '1px solid #7f1d1d88', borderRadius: 20, padding: '2px 10px', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  {clearingError === issue.clearId ? '…' : '✕ Clear'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {client ? (
        <div className="content-queue-grid" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }}>

          {/* LEFT PANEL: Generate + Schedule Blogs */}
          <div className="content-queue-left" style={{ position: 'sticky', top: 20 }}>

          {/* AI Title Generation */}
          <div className="card" style={{ padding: 22, marginBottom: 20, border: '1px solid #4f46e544' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#818cf8', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>✦ Generate Blogs with AI</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
              Titles + talking points from this client's uploaded data — scheduled straight into the queue
            </div>

            {aiError && <div className="error-box" style={{ marginBottom: 12, fontSize: 12 }}>{aiError}</div>}
            {aiSuccess && <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 7, background: '#0a1f12', border: '1px solid #16a34a55', color: '#4ade80', fontSize: 12, fontWeight: 600 }}>{aiSuccess}</div>}

            <div className="form-field">
              <label>How Many</label>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                {[1, 5, 10].map(c => (
                  <button
                    key={c}
                    type="button"
                    disabled={aiGenerating}
                    onClick={() => setAiCount(c)}
                    style={{ flex: 1, padding: '8px 0', borderRadius: 7, border: '1px solid', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: aiCount === c ? 'rgba(99,102,241,.12)' : 'var(--bg-primary)', borderColor: aiCount === c ? '#4f46e5' : 'var(--border)', color: aiCount === c ? '#818cf8' : 'var(--text-secondary)' }}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-field">
              <label>
                Focus Keywords{' '}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                  — optional, one per line
                </span>
              </label>
              <textarea
                placeholder={"Leave blank to auto-pick struggling keywords\nfrom the Marketing Report, or list topics:\nwater heater replacement\nemergency plumber"}
                value={aiKeywords}
                onChange={e => { setAiKeywords(e.target.value); setAiError(''); }}
                disabled={aiGenerating}
                style={{ minHeight: 76, fontFamily: 'inherit', lineHeight: 1.6, fontSize: 13 }}
              />
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 14, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={aiCurrentEvents}
                disabled={aiGenerating}
                onChange={e => setAiCurrentEvents(e.target.checked)}
                style={{ width: 15, height: 15, marginTop: 2, accentColor: '#4f46e5', flexShrink: 0 }}
              />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                Include current events
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 11 }}>
                  Searches the web for timely news/trends in the client's industry — lets some titles go beyond their keyword agenda
                </span>
              </span>
            </label>

            <button
              className="btn btn-primary"
              style={{ width: '100%', background: '#4f46e5', borderColor: '#4f46e5' }}
              onClick={handleAiGenerate}
              disabled={aiGenerating || queueCount >= QUEUE_MAX}
            >
              {aiGenerating ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span className="spinner" style={{ width: 14, height: 14 }}></span> Generating… ({aiCount === 1 ? '~2 min' : '~5-10 min'})
                </span>
              ) : `✦ Generate ${Math.min(aiCount, Math.max(QUEUE_MAX - queueCount, 0)) || ''} Blog${aiCount !== 1 ? 's' : ''}`}
            </button>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, textAlign: 'center' }}>
              Uses Article Type & Word Count set below
            </div>
          </div>

          {/* Manual Schedule Blogs */}
          <div className="card" style={{ padding: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 18 }}>Schedule Blogs</div>

            {bulkError && <div className="error-box" style={{ marginBottom: 14, fontSize: 12 }}>{bulkError}</div>}

            <div className="form-row" style={{ gap: 10, marginBottom: 4 }}>
              <div className="form-field" style={{ flex: 2 }}>
                <label>Default Article Type</label>
                <select value={defaultArticleType} onChange={e => setDefaultArticleType(e.target.value)} disabled={bulkAdding}>
                  {ARTICLE_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-field" style={{ flex: 1 }}>
                <label>Word Count</label>
                <select value={bulkWordCount} onChange={e => setBulkWordCount(Number(e.target.value))} disabled={bulkAdding}>
                  {[800, 1000, 1200, 1500, 2000].map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
            </div>

            <div className="form-field">
              <label>Language</label>
              <select value={bulkLanguage} onChange={e => setBulkLanguage(e.target.value)} disabled={bulkAdding}>
                <option value="en">English</option>
                <option value="es">Spanish — Español (Mexican Spanish)</option>
              </select>
            </div>

            {bulkLanguage === 'es' && (
              <div style={{ marginBottom: 14, padding: '10px 12px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderLeft: '3px solid #818cf8', borderRadius: 7, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                <strong>Spanish mode.</strong> Enter your titles in Spanish below, then click each queued item to add its talking points in Spanish. The full article, headings, FAQ, and CTA will be written in Mexican Spanish. Images stay the same; infographic labels will be Spanish.
              </div>
            )}

            <div className="form-field">
              <label>
                Blog Titles{' '}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                  — one per line, up to 10
                </span>
              </label>
              <textarea
                placeholder={bulkLanguage === 'es'
                  ? "Cómo Elegir un Buen Contratista de Techos en Salinas\n5 Señales de que Necesita Cambiar el Techo Este Otoño\nLo que Todo Dueño de Casa Debe Saber sobre los Permisos\n..."
                  : "How to Choose the Best Roofing Contractor in Salinas\n5 Signs You Need a Roof Replacement This Fall\nWhat Homeowners Need to Know About Permit Requirements\n..."}
                value={bulkTitles}
                onChange={e => { setBulkTitles(e.target.value); setBulkError(''); }}
                disabled={bulkAdding}
                style={{ minHeight: 180, fontFamily: 'inherit', lineHeight: 1.6, fontSize: 13 }}
              />
            </div>

            {/* Live preview of parsed titles */}
            {parsedTitles.length > 0 && (
              <div style={{ marginBottom: 14, padding: '10px 12px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 7 }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                  {parsedTitles.length} title{parsedTitles.length !== 1 ? 's' : ''} ready to schedule
                </div>
                {parsedTitles.map((t, i) => {
                  const warning = getCannibalizationWarning(t);
                  return (
                    <div key={i} style={{ fontSize: 12, padding: '6px 0', borderBottom: i < parsedTitles.length - 1 ? '1px solid var(--border)' : 'none', lineHeight: 1.4 }}>
                      <div style={{ color: 'var(--text-secondary)' }}>
                        <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>{i + 1}.</span>{t}
                      </div>
                      {warning && (
                        <div style={{ marginTop: 4, padding: '3px 8px', borderRadius: 5, background: '#1a0800', border: '1px solid #c2410c55', color: '#fb923c', fontSize: 11, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
                          <span style={{ flexShrink: 0 }}>⚠</span>
                          <span>Cannibalization risk — matches service page keyword "<strong>{warning.keyword}</strong>"</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <button
              className="btn btn-primary"
              style={{ width: '100%' }}
              onClick={handleBulkSchedule}
              disabled={bulkAdding || parsedTitles.length === 0}
            >
              {bulkAdding ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span className="spinner" style={{ width: 14, height: 14 }}></span> Scheduling…
                </span>
              ) : parsedTitles.length > 0
                ? `+ Schedule ${parsedTitles.length} Blog${parsedTitles.length !== 1 ? 's' : ''}`
                : '+ Schedule Blogs'}
            </button>

            {/* Queue status indicator */}
            {queueCount > 0 && (
              <div style={{
                marginTop: 14, padding: '10px 14px', borderRadius: 8, fontSize: 12, textAlign: 'center',
                background: isQueueLow ? '#1a0f00' : 'var(--bg-primary)',
                border: `1px solid ${isQueueLow ? '#a16207aa' : 'var(--border)'}`,
                color: isQueueLow ? '#eab308' : 'var(--text-secondary)',
              }}>
                {isQueueLow
                  ? `⚠ Only ${queueCount} blog${queueCount !== 1 ? 's' : ''} in queue — time to schedule more`
                  : `${queueCount} of ${QUEUE_MAX} slots filled`}
              </div>
            )}
          </div>

          </div>

          {/* RIGHT PANEL: Scheduled Queue */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {client.businessName} — Scheduled Queue
                <span style={{
                  fontSize: 11, padding: '2px 9px', borderRadius: 10, border: '1px solid',
                  background: isQueueLow ? '#1a0f00' : 'var(--bg-secondary)',
                  borderColor: isQueueLow ? '#a16207aa' : 'var(--border)',
                  color: isQueueLow ? '#eab308' : 'var(--text-muted)',
                }}>
                  {queueCount} / {QUEUE_MAX}
                </span>
                {isQueueLow && (
                  <span style={{ fontSize: 11, background: '#1a0f00', border: '1px solid #a16207aa', color: '#eab308', padding: '2px 9px', borderRadius: 10 }}>
                    ⚠ Add more soon
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Drag ⠿ to reorder · Click a row to edit the title & details
              </div>
            </div>

            {genError && <div className="error-box" style={{ margin: '12px 16px', fontSize: 12 }}>{genError}</div>}

            {loadingPosts ? (
              <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner"></div></div>
            ) : scheduledQueue.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '52px 20px', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>✦</div>
                <div style={{ fontSize: 13 }}>No scheduled blogs — paste titles in the panel on the left to get started</div>
              </div>
            ) : (
              <div className="queue-rows-wrap">
                {scheduledQueue.map((post, index) => {
                  const isExpanded = expandedPostId === post.id;
                  const isDraggingThis = draggedIndex === index;
                  const isDragOver = dragOverIndex === index && draggedIndex !== index;
                  const cannibalWarning = getCannibalizationWarning(post.keyword || post.title);
                  // AI-generated titles can carry any value in 900–2000, so keep the
                  // current one as an option even when it isn't on the standard list
                  const currentWordCount = wordCounts[post.id] ?? post.wordCount ?? DEFAULT_WORD_COUNT;
                  const wordCountOptions = WORD_COUNTS.includes(currentWordCount)
                    ? WORD_COUNTS
                    : [...WORD_COUNTS, currentWordCount].sort((a, b) => a - b);

                  return (
                    <div
                      key={post.id}
                      draggable
                      onDragStart={e => handleDragStart(e, index)}
                      onDragOver={e => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      onDrop={e => handleDrop(e, index)}
                      style={{
                        borderBottom: '1px solid var(--border)',
                        opacity: isDraggingThis ? 0.4 : 1,
                        background: isDragOver ? 'rgba(99,102,241,.07)' : 'transparent',
                        borderTop: isDragOver ? '2px solid #818cf8' : '2px solid transparent',
                        transition: 'background .1s',
                      }}
                    >
                      {/* Main row — fixed-width columns so nothing overflows */}
                      <div className="queue-row-main" style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', gap: 10, minWidth: 0 }}>

                        {/* Drag handle: 20px (hidden on mobile — drag-reorder isn't usable on touch) */}
                        <div
                          className="queue-row-handle"
                          style={{ width: 20, flexShrink: 0, color: 'var(--text-muted)', cursor: 'grab', fontSize: 18, lineHeight: 1, userSelect: 'none', textAlign: 'center' }}
                          title="Drag to reorder"
                        >
                          ⠿
                        </div>

                        {/* Title + hint (clickable) — takes all remaining space */}
                        <div
                          className="queue-row-title"
                          style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                          onClick={() => {
                            if (!isExpanded) {
                              setDetailTexts(d => ({ ...d, [post.id]: post.additionalDetails ?? '' }));
                              setTitleTexts(t => ({ ...t, [post.id]: post.title ?? '' }));
                              setWordCounts(w => ({ ...w, [post.id]: post.wordCount ?? DEFAULT_WORD_COUNT }));
                            }
                            setExpandedPostId(isExpanded ? null : post.id);
                          }}
                        >
                          <div style={{ fontWeight: 500, color: 'var(--text-primary)', overflowWrap: 'anywhere', fontSize: 13, lineHeight: '1.4' }}>
                            {post.title || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(No title)</span>}
                          </div>
                          {(
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                              <span
                                title={post.wordCount ? 'Target word count for this article — change it in the expanded panel' : `Not set — the generator defaults to ${DEFAULT_WORD_COUNT.toLocaleString('en-US')} words`}
                                style={{ padding: '1px 7px', borderRadius: 20, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: post.wordCount ? 'var(--text-secondary)' : 'var(--text-muted)' }}
                              >
                                <span style={{ fontWeight: 600 }}>{(post.wordCount || DEFAULT_WORD_COUNT).toLocaleString('en-US')}</span> words{post.wordCount ? '' : ' (default)'}
                              </span>
                              {post.headKeyword && (
                                <span title="Head term the demand gate scored (geo/qualifiers stripped)">🔍 <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{post.headKeyword}</span></span>
                              )}
                              {post.demandVolume != null && (
                                <span title="Google Ads monthly search volume for the head term"><span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{post.demandVolume.toLocaleString('en-US')}</span>/mo</span>
                              )}
                              {post.searchIntent && (
                                <span title="DataForSEO search intent" style={{ textTransform: 'capitalize', padding: '1px 7px', borderRadius: 20, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>{post.searchIntent}</span>
                              )}
                              {post.regulatoryHook && (
                                <span title={`Regulatory hook${post.hookEffectiveDate ? ` · effective ${post.hookEffectiveDate}` : ''} · verified by ${(post.hookSources || []).filter(s => s.verified).length} independent sources · took the next open blog day`} style={{ padding: '1px 7px', borderRadius: 20, border: '1px solid #0f172a', background: '#f8fafc', color: '#0f172a', fontWeight: 700 }}>Hook</span>
                              )}
                              {(post.topicTier || post.tierOverride) && (
                                <span title={[post.topicPattern && post.topicPattern !== 'other' ? `Pattern: ${post.topicPattern.replace('_', ' ')}` : null, ...(post.topicReasons || [])].filter(Boolean).join('\n')}>
                                  <TierChip tier={post.topicTier} override={post.tierOverride} />
                                </span>
                              )}
                              {post.persona && (
                                <span title="Customer persona this post is written for (from the client's Personas document)">👤 <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{post.persona}</span></span>
                              )}
                            </div>
                          )}
                          {cannibalWarning ? (
                            <div style={{ fontSize: 11, color: '#fb923c', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              ⚠ Cannibalization risk — "{cannibalWarning.keyword}"
                            </div>
                          ) : (
                            <div style={{ fontSize: 11, color: post.additionalDetails ? '#818cf8' : 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {post.additionalDetails ? '📝 Has details · click to edit title & details' : 'Click to edit title or add details'}
                            </div>
                          )}
                        </div>

                        {/* Per-row article type dropdown: 130px */}
                        <select
                          className="queue-row-type"
                          value={post.articleType || 'Informational'}
                          onChange={e => { e.stopPropagation(); handleUpdateArticleType(post.id, e.target.value); }}
                          onClick={e => e.stopPropagation()}
                          style={{ width: 130, flexShrink: 0, fontSize: 11, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 5, padding: '3px 6px', color: 'var(--text-secondary)', cursor: 'pointer' }}
                        >
                          {ARTICLE_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>

                        {/* Status badge: 100px */}
                        <div className="queue-row-status" style={{ width: 100, flexShrink: 0 }}>
                          <StatusBadge status={post.status} />
                        </div>

                        {/* Scheduled date: 90px */}
                        <div className="queue-row-date" style={{ width: 90, flexShrink: 0, fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {formatScheduled(post.scheduledFor)}
                        </div>

                        {/* Actions: 110px */}
                        <div className="queue-row-actions" style={{ width: 110, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                          {(post.status === 'planned' || post.status === 'future') && (
                            <button
                              className="btn btn-sm btn-secondary queue-row-generate"
                              onClick={() => handleGenerateFromPlan(post)}
                              disabled={!!generatingId || !hasWpKey}
                              style={{ fontSize: 11 }}
                              title={!hasWpKey ? 'No WP API key on this client' : 'Generate this blog now'}
                            >
                              {generatingId === post.id ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span className="spinner" style={{ width: 12, height: 12 }}></span>
                                  <span className="queue-generate-label">Generating…</span>
                                </span>
                              ) : (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  ⚡<span className="queue-generate-label">Generate</span>
                                </span>
                              )}
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(post.id)}
                            disabled={!!generatingId}
                          >🗑</button>
                        </div>
                      </div>

                      {/* Expanded: additional details form */}
                      {isExpanded && (
                        <div style={{ padding: '0 16px 16px 46px', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8, paddingTop: 14 }}>
                            Blog Title
                          </div>
                          <input
                            type="text"
                            value={titleTexts[post.id] ?? post.title ?? ''}
                            onChange={e => setTitleTexts(t => ({ ...t, [post.id]: e.target.value }))}
                            style={{ width: '100%', marginBottom: 12, fontFamily: 'inherit', fontSize: 13, fontWeight: 500, boxSizing: 'border-box', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 7, padding: '8px 12px', color: 'var(--text-primary)', outline: 'none' }}
                          />
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                            Target Word Count
                          </div>
                          <select
                            value={currentWordCount}
                            onChange={e => setWordCounts(w => ({ ...w, [post.id]: Number(e.target.value) }))}
                            style={{ width: 160, marginBottom: 12, fontSize: 13, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 7, padding: '8px 12px', color: 'var(--text-primary)', outline: 'none', cursor: 'pointer' }}
                          >
                            {wordCountOptions.map(w => <option key={w} value={w}>{w.toLocaleString('en-US')} words</option>)}
                          </select>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                            Additional Details for This Blog
                          </div>
                          <textarea
                            placeholder="Add specific points, angles, keywords, local references, or any context to include when writing this article..."
                            value={detailTexts[post.id] ?? ''}
                            onChange={e => setDetailTexts(d => ({ ...d, [post.id]: e.target.value }))}
                            style={{ width: '100%', minHeight: 100, marginBottom: 10, fontFamily: 'inherit', lineHeight: 1.5, fontSize: 13, boxSizing: 'border-box' }}
                          />
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleSaveDetail(post)}
                              disabled={savingDetailId === post.id}
                            >
                              {savingDetailId === post.id ? 'Saving…' : 'Save Changes'}
                            </button>
                            <button className="btn btn-sm btn-secondary" onClick={() => setExpandedPostId(null)}>
                              Close
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Footer note when at max */}
                {queueCount >= QUEUE_MAX && (
                  <div style={{ padding: '12px 20px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', borderTop: '1px solid var(--border)' }}>
                    Queue is full ({QUEUE_MAX} blogs). Generate or remove some before adding more.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: '64px 20px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>✦</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Select a client to get started</div>
          <div style={{ fontSize: 13 }}>Choose a client above to view their content queue and schedule new articles</div>
        </div>
      )}

      {selectedClientId && publishedHistory.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-title" style={{ marginBottom: 4 }}>
            Published History
            <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>{publishedHistory.length} in the last {HISTORY_DAYS} days</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: (gscSource || blogGscLoading || blogGscError) ? 4 : 14 }}>Rolling {HISTORY_DAYS}-day window of articles published for this client</div>
          {(gscSource || blogGscLoading || blogGscError) && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {gscSource === 'recent' && (
                <span>Search Console · last {blogGsc.windowDays} days ({blogGsc.startDate} → {blogGsc.endDate})</span>
              )}
              {gscSource === 'monthly' && (
                <span>
                  Search Console · monthly snapshot {gscSnapshot.startDate} → {gscSnapshot.endDate}
                  {gscSnapshot.pulledAt?.toDate ? ` · updated ${gscSnapshot.pulledAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                </span>
              )}
              {blogGscLoading && <span>Loading Search Console…</span>}
              {blogGscError && !blogGscLoading && <span style={{ color: '#f87171' }}>⚠ {blogGscError}</span>}
              <button
                type="button"
                onClick={() => loadBlogGsc(selectedClientId, true)}
                disabled={blogGscLoading}
                title="Pull fresh Search Console numbers now"
                style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, cursor: blogGscLoading ? 'default' : 'pointer', border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-secondary)' }}
              >
                {blogGscLoading ? 'Refreshing…' : '↻ Refresh'}
              </button>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {publishedHistory.map((post, idx) => {
              const dateStr = post.dateMs ? new Date(post.dateMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
              const detail = detailByUrl[post.postUrl];
              const isExpanded = expandedUrl === post.postUrl;
              const canExpand = !!post.postUrl && !post.isDraft;
              return (
                <div key={post.id} style={{ borderTop: idx === 0 ? 'none' : '1px solid var(--border)' }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', gap: 12 }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {post.postUrl ? (
                      <a href={post.postUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                        {post.title}
                      </a>
                    ) : (
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{post.title}</span>
                    )}
                    {(() => {
                      const m = gscByUrl.get(normUrl(post.postUrl));
                      if (m) {
                        return (
                          <div style={{ display: 'flex', gap: 14, marginTop: 3, fontSize: 11, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                            <span><span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{fmtNum(m.impressions)}</span> impressions</span>
                            <span><span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{fmtNum(m.clicks)}</span> clicks</span>
                            <span>avg pos <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{m.position}</span></span>
                          </div>
                        );
                      }
                      if (gscSource && !post.isDraft) {
                        // Distinguish "too new to have data" from "live but not earning
                        // impressions" — GSC itself reports ~3 days behind.
                        const daysLive = post.dateMs ? Math.floor((Date.now() - post.dateMs) / 86400000) : null;
                        const msg = daysLive != null && daysLive < 7
                          ? 'Just published — Search Console reports about 3 days behind'
                          : gscSource === 'recent'
                            ? `No impressions in the last ${blogGsc.windowDays} days`
                            : 'No Search Console impressions yet';
                        return <div style={{ marginTop: 3, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>{msg}</div>;
                      }
                      return null;
                    })()}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: post.isDraft ? 'var(--bg-tertiary)' : '#0a1f12', color: post.isDraft ? 'var(--text-muted)' : '#4ade80', border: '1px solid', borderColor: post.isDraft ? 'var(--border)' : '#16a34a55', fontWeight: 600 }}>
                      {post.isDraft ? 'Draft' : 'Published'}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{dateStr}</span>
                    {canExpand && (
                      <button
                        type="button"
                        onClick={() => toggleDetail(post.postUrl)}
                        title="Search Console queries for this post"
                        style={{ fontSize: 11, padding: '3px 9px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--border)', background: isExpanded ? 'var(--bg-tertiary)' : 'var(--bg-primary)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}
                      >
                        {isExpanded ? '▾' : '▸'} queries
                      </button>
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div style={{ padding: '2px 0 14px' }}>
                    {detail?.loading && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading Search Console data…</div>}
                    {detail?.error && <div style={{ fontSize: 12, color: '#f87171' }}>⚠ {detail.error}</div>}
                    {detail?.data && (() => {
                      const d = detail.data;
                      if (d.unavailable) {
                        return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{d.unavailable === 'disconnected' ? 'Search Console is disconnected for this client.' : 'No Search Console property is connected for this client.'}</div>;
                      }
                      const p = d.page;
                      const stat = (label, val) => (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{val}</span>
                          <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</span>
                        </div>
                      );
                      return (
                        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>Live · last {d.windowDays} days ({d.startDate} → {d.endDate})</div>
                          {p ? (
                            <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginBottom: 14 }}>
                              {stat('Impressions', fmtNum(p.impressions))}
                              {stat('Clicks', fmtNum(p.clicks))}
                              {stat('CTR', `${p.ctr}%`)}
                              {stat('Avg position', p.position)}
                            </div>
                          ) : (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>No impressions for this URL in the window (too new, not indexed, or a URL mismatch).</div>
                          )}
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>Queries this post ranks for</div>
                          {d.queries && d.queries.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 64px 56px 52px', gap: '3px 12px', fontSize: 12 }}>
                              <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Query</span>
                              <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.4px', textAlign: 'right' }}>Impr</span>
                              <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.4px', textAlign: 'right' }}>Clicks</span>
                              <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.4px', textAlign: 'right' }}>Pos</span>
                              {d.queries.map((q, qi) => (
                                <React.Fragment key={qi}>
                                  <span style={{ color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={q.query}>{q.query}</span>
                                  <span style={{ color: 'var(--text-secondary)', textAlign: 'right' }}>{fmtNum(q.impressions)}</span>
                                  <span style={{ color: 'var(--text-secondary)', textAlign: 'right' }}>{fmtNum(q.clicks)}</span>
                                  <span style={{ color: 'var(--text-secondary)', textAlign: 'right' }}>{q.position}</span>
                                </React.Fragment>
                              ))}
                            </div>
                          ) : (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No queries with impressions in this window.</div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
